import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Drawer, Input, Table } from 'antd';
import { DollarOutlined, FileDoneOutlined, PieChartOutlined, TeamOutlined } from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import styles from './salesDocumentListingsDrawer.module.css';
import { fetchCustomerListings } from '../../../services/api';

export type ListingType = 'enquiries' | 'quotations' | 'sales_orders' | 'sales_invoices';

type SalesDocumentRow = {
  document_id?: number;
  document_number?: string | null;
  document_date?: string | null;
  enquiry_number?: string | null;
  quotation_number?: string | null;
  sales_order_number?: string | null;
  customer_id?: number | null;
  customer_name?: string | null;
  sales_rep_name?: string | null;
  total_quantity?: number | null;
  total_amount?: number | null;
  purchase_amount?: number | null;
  profit_amount?: number | null;
  status?: string | null;
  invoice_numbers?: string | null;
  payment_term?: string | null;
};

type ListingsSummary = {
  total_records?: number;
  total_amount?: number;
  total_quantity?: number;
  purchase_amount?: number;
  profit_amount?: number;
  unique_customers?: number;
  unique_skus?: number;
};

type ListingsResponse = {
  data?: SalesDocumentRow[];
  summary?: ListingsSummary;
  total?: number;
};

type DateRange = {
  start: string;
  end: string;
};

type Props = {
  open: boolean;
  listingType: ListingType;
  customerId: string | number;
  customerName?: string | null;
  dateRange: DateRange;
  onClose: () => void;
};

type SummaryCard = {
  label: string;
  value: string;
  onClick?: () => void;
};

type SalesOrderSummaryCard = {
  label: string;
  value: string;
  gradient: string;
  icon: React.ReactNode;
  iconBg?: string;
  valueColor?: string;
  onClick?: () => void;
  highlight?: boolean;
};

const LISTING_TYPE_LABELS: Record<ListingType, string> = {
  enquiries: 'Enquiries',
  quotations: 'Quotations',
  sales_orders: 'Sales Orders',
  sales_invoices: 'Sales Invoices',
};

const SUMMARY_CARD_STYLES = [
  'linear-gradient(135deg, #a5b4fc, #c7d2fe)',
  'linear-gradient(135deg, #fecdd3, #fda4af)',
  'linear-gradient(135deg, #bbf7d0, #86efac)',
  'linear-gradient(135deg, #bae6fd, #93c5fd)',
];

const formatNumber = (value?: number | null) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '-';
  return Number(value).toLocaleString();
};

const formatCurrency = (value?: number | null) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const SalesDocumentListingsDrawer = ({
  open,
  listingType,
  customerId,
  customerName,
  dateRange,
  onClose,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SalesDocumentRow[]>([]);
  const [summary, setSummary] = useState<ListingsSummary | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<string>('document_date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const fetchData = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const response: ListingsResponse = await fetchCustomerListings({
        listingType,
        customerId,
        dateFrom: dateRange.start,
        dateTo: dateRange.end,
        sortBy,
        sortOrder,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      const rows = Array.isArray(response?.data) ? response.data : [];
      setData(rows);
      setSummary(response?.summary ?? null);
      setTotal(
        response?.summary?.total_records ??
          (typeof response?.total === 'number' ? response.total : rows.length),
      );
    } catch (error) {
      setData([]);
      setSummary(null);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [open, listingType, customerId, dateRange.start, dateRange.end, sortBy, sortOrder, pageSize, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [listingType, customerId, dateRange.start, dateRange.end, searchQuery]);

  const columns = useMemo<ColumnsType<SalesDocumentRow>>(() => {
    const renderStatus = (value: string | null) => {
      if (!value) return '-';
      let statusLabel = value;
      if (listingType === 'sales_invoices') {
        if (value === '1') statusLabel = 'Unpaid';
        if (value === '2') statusLabel = 'Paid';
        if (value === '3') statusLabel = 'Partially Paid';
      }
      return statusLabel;
    };

    const baseColumns: ColumnsType<SalesDocumentRow> = [
      {
        title: 'Document Date',
        dataIndex: 'document_date',
        key: 'document_date',
        width: 160,
        sorter: true,
        render: (value: string) => formatDate(value),
      },
      {
        title: 'Document #',
        dataIndex: 'document_number',
        key: 'document_number',
        width: 180,
        render: (value: string | null) => value || '-',
      },
      {
        title: 'Customer',
        dataIndex: 'customer_name',
        key: 'customer_name',
        width: 220,
        render: (value: string | null) => value || '-',
      },
      {
        title: 'Sales Rep',
        dataIndex: 'sales_rep_name',
        key: 'sales_rep_name',
        width: 180,
        render: (value: string | null) => value || '-',
      },
      {
        title: 'Total Qty',
        dataIndex: 'total_quantity',
        key: 'total_quantity',
        width: 120,
        align: 'right',
        sorter: true,
        render: (value: number | null) => formatNumber(value),
      },
      {
        title: 'Total Amount',
        dataIndex: 'total_amount',
        key: 'total_amount',
        width: 160,
        align: 'right',
        sorter: true,
        render: (value: number | null) => formatCurrency(value),
      },
    ];

    if (listingType === 'quotations') {
      return [
        {
          title: 'Enquiry #',
          dataIndex: 'enquiry_number',
          key: 'enquiry_number',
          width: 160,
          render: (value: string | null) => value || '-',
        },
        {
          title: 'Quotation #',
          dataIndex: 'quotation_number',
          key: 'quotation_number',
          width: 160,
          render: (value: string | null) => value || '-',
        },
        {
          title: 'Quotations #',
          dataIndex: 'document_number',
          key: 'document_number',
          width: 160,
          render: (value: string | null) => value || '-',
        },
        {
          title: 'Quotation Date',
          dataIndex: 'document_date',
          key: 'document_date',
          width: 160,
          sorter: true,
          render: (value: string) => formatDate(value),
        },
        {
          title: 'Customer',
          dataIndex: 'customer_name',
          key: 'customer_name',
          width: 220,
          render: (value: string | null) => value || '-',
        },
        {
          title: 'Sales Rep',
          dataIndex: 'sales_rep_name',
          key: 'sales_rep_name',
          width: 180,
          render: (value: string | null) => value || '-',
        },
        {
          title: 'Total Qty',
          dataIndex: 'total_quantity',
          key: 'total_quantity',
          width: 120,
          align: 'right',
          sorter: true,
          render: (value: number | null) => formatNumber(value),
        },
        {
          title: 'Status',
          dataIndex: 'status',
          key: 'status',
          width: 120,
          align: 'right',
          render: (value: string | null) => renderStatus(value),
        },
        {
          title: 'Total Amount',
          dataIndex: 'total_amount',
          key: 'total_amount',
          width: 160,
          align: 'right',
          sorter: true,
          render: (value: number | null) => formatCurrency(value),
        },
      ];
    }

    if (listingType === 'sales_invoices') {
      return [
        {
          title: 'Invoice Date',
          dataIndex: 'document_date',
          key: 'document_date',
          width: 160,
          sorter: true,
          render: (value: string) => formatDate(value),
        },
        {
          title: 'Invoice #',
          dataIndex: 'document_number',
          key: 'document_number',
          width: 180,
          render: (value: string | null) => value || '-',
        },
        {
          title: 'Sales Order #',
          dataIndex: 'sales_order_number',
          key: 'sales_order_number',
          width: 180,
          render: (value: string | null) => value || '-',
        },
        {
          title: 'Customer',
          dataIndex: 'customer_name',
          key: 'customer_name',
          width: 220,
          render: (value: string | null) => value || '-',
        },
        {
          title: 'Sales Rep',
          dataIndex: 'sales_rep_name',
          key: 'sales_rep_name',
          width: 180,
          render: (value: string | null) => value || '-',
        },
        {
          title: 'Total Qty',
          dataIndex: 'total_quantity',
          key: 'total_quantity',
          width: 120,
          align: 'right',
          sorter: true,
          render: (value: number | null) => formatNumber(value),
        },
        {
          title: 'Status',
          dataIndex: 'status',
          key: 'status',
          width: 120,
          align: 'right',
          render: (value: string | null) => renderStatus(value),
        },
        {
          title: 'Total Amount',
          dataIndex: 'total_amount',
          key: 'total_amount',
          width: 160,
          align: 'right',
          sorter: true,
          render: (value: number | null) => formatCurrency(value),
        },
        {
          title: 'Purchase Amount',
          dataIndex: 'purchase_amount',
          key: 'purchase_amount',
          width: 150,
          align: 'right',
          render: (value: number | null) => formatCurrency(value),
        },
        {
          title: 'Profit Amount',
          dataIndex: 'profit_amount',
          key: 'profit_amount',
          width: 150,
          align: 'right',
          render: (value: number | null) => formatCurrency(value),
        },
      ];
    }

    if (listingType === 'sales_orders') {
      return [
        {
          title: 'SO Date',
          dataIndex: 'document_date',
          key: 'document_date',
          width: 160,
          sorter: true,
          render: (value: string) => formatDate(value),
        },
        {
          title: 'SO #',
          dataIndex: 'document_number',
          key: 'document_number',
          width: 180,
          render: (value: string | null) => value || '-',
        },
        {
          title: 'Invoice #',
          dataIndex: 'invoice_numbers',
          key: 'invoice_numbers',
          width: 180,
          render: (value: string | null) => value || '-',
        },
        {
          title: 'Customer',
          dataIndex: 'customer_name',
          key: 'customer_name',
          width: 220,
          render: (value: string | null) => value || '-',
        },
        {
          title: 'Payment Term',
          dataIndex: 'payment_term',
          key: 'payment_term',
          width: 140,
          render: (value: string | null) => value || '-',
        },
        {
          title: 'Sales Rep',
          dataIndex: 'sales_rep_name',
          key: 'sales_rep_name',
          width: 180,
          render: (value: string | null) => value || '-',
        },
        {
          title: 'Total Qty',
          dataIndex: 'total_quantity',
          key: 'total_quantity',
          width: 120,
          align: 'right',
          sorter: true,
          render: (value: number | null) => formatNumber(value),
        },
        {
          title: 'Total Amount',
          dataIndex: 'total_amount',
          key: 'total_amount',
          width: 160,
          align: 'right',
          sorter: true,
          render: (value: number | null) => formatCurrency(value),
        },
        {
          title: 'Purchase Amount',
          dataIndex: 'purchase_amount',
          key: 'purchase_amount',
          width: 150,
          align: 'right',
          render: (value: number | null) => formatCurrency(value),
        },
        {
          title: 'Profit',
          dataIndex: 'profit_amount',
          key: 'profit_amount',
          width: 150,
          align: 'right',
          render: (value: number | null) => formatCurrency(value),
        },
      ];
    }

    if (listingType === 'enquiries') {
      return [
        {
          title: 'Enquiry #',
          dataIndex: 'document_number',
          key: 'document_number',
          width: 180,
          render: (value: string | null) => value || '-',
        },
        {
          title: 'Enquiry Date',
          dataIndex: 'document_date',
          key: 'document_date',
          width: 160,
          sorter: true,
          render: (value: string) => formatDate(value),
        },
        {
          title: 'Customer',
          dataIndex: 'customer_name',
          key: 'customer_name',
          width: 220,
          render: (value: string | null) => value || '-',
        },
        {
          title: 'Sales Rep',
          dataIndex: 'sales_rep_name',
          key: 'sales_rep_name',
          width: 180,
          render: (value: string | null) => value || '-',
        },
      ];
    }

    return baseColumns;
  }, [listingType]);

  const summaryCards = useMemo<Array<SummaryCard | SalesOrderSummaryCard>>(() => {
    if (!summary) {
      return [];
    }
    if (listingType === 'sales_orders') {
      const totalSaleAmount = Number(summary.total_amount ?? 0);
      const totalPurchaseAmount = Number(summary.purchase_amount ?? 0);
      const grossProfit = totalSaleAmount - totalPurchaseAmount;
      const grossMargin = totalSaleAmount > 0 ? (grossProfit / totalSaleAmount) * 100 : 0;
      const uniqueSkus = Number(summary.unique_skus ?? 0);
      const uniqueCustomers = Number(summary.unique_customers ?? 0);
      const avgRevenuePerCustomer = uniqueCustomers > 0 ? totalSaleAmount / uniqueCustomers : 0;

      const salesOrderCards: SalesOrderSummaryCard[] = [
        {
          label: 'Orders',
          value: formatNumber(summary.total_records ?? 0),
          gradient: 'linear-gradient(135deg, #e0f2fe, #c7d2fe)',
          icon: <FileDoneOutlined style={{ color: '#1f2937' }} />,
          iconBg: 'rgba(148,163,184,0.26)',
        },
        {
          label: 'Sale Amount',
          value: formatCurrency(totalSaleAmount),
          gradient: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
          icon: <DollarOutlined style={{ color: '#1d4ed8' }} />,
          iconBg: 'rgba(59,130,246,0.24)',
          highlight: true,
        },
        {
          label: 'Purchase Amount',
          value: formatCurrency(totalPurchaseAmount),
          gradient: 'linear-gradient(135deg, #ede9fe, #ddd6fe)',
          icon: <FileDoneOutlined style={{ color: '#9333ea' }} />,
          iconBg: 'rgba(168,85,247,0.24)',
        },
        {
          label: 'Gross Profit',
          value: formatCurrency(grossProfit),
          gradient:
            grossProfit >= 0
              ? 'linear-gradient(135deg, #dcfce7, #bbf7d0)'
              : 'linear-gradient(135deg, #fee2e2, #fecdd3)',
          icon: (
            <DollarOutlined style={{ color: grossProfit >= 0 ? '#047857' : '#dc2626' }} />
          ),
          iconBg: grossProfit >= 0 ? 'rgba(34,197,94,0.22)' : 'rgba(248,113,113,0.24)',
        },
        {
          label: 'Gross Margin',
          value: `${grossMargin.toFixed(2)}%`,
          gradient:
            grossMargin >= 0
              ? 'linear-gradient(135deg, #e0f2fe, #c7d2fe)'
              : 'linear-gradient(135deg, #ffe4e6, #fed7aa)',
          icon: (
            <PieChartOutlined style={{ color: grossMargin >= 0 ? '#16a34a' : '#cf1322' }} />
          ),
          iconBg: grossMargin >= 0 ? 'rgba(59,130,246,0.24)' : 'rgba(251,146,60,0.24)',
        },
        {
          label: 'Unique SKUs',
          value: formatNumber(uniqueSkus),
          gradient: 'linear-gradient(135deg, #ede9fe, #ddd6fe)',
          icon: <PieChartOutlined style={{ color: '#7c3aed' }} />,
          iconBg: 'rgba(167,139,250,0.24)',
        },
        {
          label: 'Unique Customers',
          value: formatNumber(uniqueCustomers),
          gradient: 'linear-gradient(135deg, #e0f2fe, #c7d2fe)',
          icon: <TeamOutlined style={{ color: '#1f2937' }} />,
          iconBg: 'rgba(148,163,184,0.26)',
        },
        {
          label: 'Avg Revenue / Customer',
          value: formatCurrency(avgRevenuePerCustomer),
          gradient: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
          icon: <DollarOutlined style={{ color: '#1d4ed8' }} />,
          iconBg: 'rgba(59,130,246,0.24)',
        },
      ];

      return salesOrderCards;
    }

    const cards: SummaryCard[] = [
      {
        label: 'Total Amount',
        value: formatCurrency(summary.total_amount ?? 0),
      },
      {
        label: 'Total Quantity',
        value: formatNumber(summary.total_quantity ?? 0),
      },
      {
        label: 'Total Records',
        value: formatNumber(summary.total_records ?? 0),
      },
    ];

    if (listingType === 'quotations' || listingType === 'sales_invoices') {
      cards.splice(1, 0, {
        label: 'Unique Customers',
        value: formatNumber(summary.unique_customers ?? 0),
      });
      cards.splice(2, 0, {
        label: 'Unique SKUs',
        value: formatNumber(summary.unique_skus ?? 0),
      });
    }

    if (listingType === 'sales_invoices') {
      cards.splice(1, 0, {
        label: 'Purchase Amount',
        value: formatCurrency(summary.purchase_amount ?? 0),
      });
      cards.splice(2, 0, {
        label: 'Profit Amount',
        value: formatCurrency(summary.profit_amount ?? 0),
      });
    }

    return cards;
  }, [listingType, summary]);

  const pagination: TablePaginationConfig = {
    current: page,
    pageSize,
    total,
    showSizeChanger: true,
    showTotal: (totalCount, range) => `${range[0]}-${range[1]} of ${totalCount} items`,
    pageSizeOptions: ['10', '25', '50', '100'],
  };

  const handleTableChange: TablePaginationConfig['onChange'] = (nextPage, nextPageSize) => {
    if (nextPage !== page || nextPageSize !== pageSize) {
      setPage(nextPage);
      setPageSize(nextPageSize);
    }
  };

  const filterLabel = customerName ? customerName : `Customer #${customerId}`;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      destroyOnClose
      width={1400}
      title={
        <div className={styles.drawerTitle}>
          <div>
            <span>{LISTING_TYPE_LABELS[listingType]}</span>
            <span className={styles.filterLabel}>{filterLabel}</span>
          </div>
          <span className={styles.drawerSubtitle}>
            {formatDate(dateRange.start)} - {formatDate(dateRange.end)}
          </span>
        </div>
      }
      className={styles.listingsDrawer}
    >
        <div className={styles.drawerContent}>
          {listingType === 'sales_orders' ? (
            <div className={styles.skuSummaryRow}>
              {summaryCards.map((card) => {
                const salesCard = card as SalesOrderSummaryCard;
                const content = (
                  <>
                    <div className={styles.skuSummaryContent}>
                      <span className={styles.skuSummaryLabel}>{salesCard.label}</span>
                      <strong
                        className={`${styles.skuSummaryValue} ${salesCard.highlight ? styles.skuSummaryValueLarge : ''}`}
                        style={{ color: salesCard.valueColor ?? undefined }}
                      >
                        {salesCard.value}
                      </strong>
                    </div>
                    <div
                      className={styles.skuSummaryIcon}
                      style={{ background: salesCard.iconBg ?? undefined }}
                    >
                      {salesCard.icon}
                    </div>
                  </>
                );
                return salesCard.onClick ? (
                  <button
                    key={salesCard.label}
                    type="button"
                    className={`${styles.skuSummaryCard} ${salesCard.highlight ? styles.skuSummaryCardHighlight : ''}`}
                    style={{ background: salesCard.gradient }}
                    onClick={salesCard.onClick}
                  >
                    {content}
                  </button>
                ) : (
                  <div
                    key={salesCard.label}
                    className={`${styles.skuSummaryCard} ${salesCard.highlight ? styles.skuSummaryCardHighlight : ''}`}
                    style={{ background: salesCard.gradient }}
                  >
                    {content}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={styles.summaryRow}>
              {summaryCards.map((card, index) =>
                card.onClick ? (
                  <button
                    key={card.label}
                    type="button"
                    className={styles.summaryCardButton}
                    onClick={card.onClick}
                    style={{ background: SUMMARY_CARD_STYLES[index % SUMMARY_CARD_STYLES.length] }}
                  >
                    <span>{card.label}</span>
                    <strong
                      className={
                        card.label === 'Unique SKUs' || card.label === 'Unique Customers'
                          ? styles.summaryValueLarge
                          : undefined
                      }
                    >
                      {card.value}
                    </strong>
                  </button>
                ) : (
                  <div
                    key={card.label}
                    className={styles.summaryCard}
                    style={{ background: SUMMARY_CARD_STYLES[index % SUMMARY_CARD_STYLES.length] }}
                  >
                    <span>{card.label}</span>
                    <strong
                      className={
                        card.label === 'Unique SKUs' || card.label === 'Unique Customers'
                          ? styles.summaryValueLarge
                          : undefined
                      }
                    >
                      {card.value}
                    </strong>
                  </div>
                ),
              )}
            </div>
          )}
          <div className={styles.toolbar}>
            <Input.Search
              placeholder={`Search ${LISTING_TYPE_LABELS[listingType]} documents`}
            value={searchValue}
            allowClear
            onChange={(event) => setSearchValue(event.target.value)}
            onSearch={(value) => setSearchQuery(value)}
            style={{ maxWidth: 360 }}
          />
        </div>
        <Table<SalesDocumentRow>
          rowKey={(record, index) =>
            record.document_id ??
            record.document_number ??
            `${record.customer_id ?? 'row'}-${record.document_date ?? 'na'}-${index ?? 0}`
          }
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={pagination}
          scroll={{ x: 1200 }}
          onChange={(nextPagination, _filters, sorter) => {
            handleTableChange(nextPagination.current ?? page, nextPagination.pageSize ?? pageSize);
            if (!Array.isArray(sorter) && sorter.field) {
              setSortBy(sorter.field as string);
              setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
            }
          }}
        />
      </div>
    </Drawer>
  );
};

export default SalesDocumentListingsDrawer;
